import { useRef, useState } from 'react';
import { type Matcher } from 'react-day-picker';

import { useDateFormatter } from '@mint-lib/formatting-hooks';
import { useDateLocalize } from '@mint-lib/formatting-hooks/locales';
import { addYears, endOfYear, format } from 'date-fns';

import { useTranslation } from '../../../../i18n/src/i18n.js';
import { Button } from '../../lib/button.jsx';
import { Calendar } from '../../lib/calendar.jsx';
import { Input } from '../../lib/input.jsx';
import { SelectNavigation } from './Navigation.jsx';

type CalendarSingleProps = {
  /**
   * Selected date
   */
  selected: Date | undefined;
  /**
   * Callback for selecting a date. It will be called when the date is selected.
   */
  onSelect: (date?: Date) => void;
  /**
   * Callback that applies the selected date range and sidebar selected button. It will be called when the `Apply` button is clicked.
   */
  onApply: (date?: Date) => void;
  /**
   * Helps to disable dates in various ways
   */
  disabledDates?: Matcher | Matcher[];
  /**
   * Event dates to be highlighted in the calendar
   */
  eventDates?: Date[];
  /**
   * Month to show if no date is selected
   */
  defaultMonth?: Date;
};
const CalendarSingle = (props: CalendarSingleProps) => {
  const calendarRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation('@mint-lib/tw-components');
  const [month, setMonth] = useState<Date | undefined>(
    props.selected ?? props.defaultMonth,
  );
  const localize = useDateLocalize();
  const { formattedDateFrom } = useDateFormatter(props.selected);

  const handleDateApply = () => {
    props.onApply?.(props.selected);
  };

  return (
    <div className="flex w-full" ref={calendarRef}>
      <Calendar
        {...props}
        data-testid="calendar-single"
        mode="single"
        showOutsideDays={false}
        hideNavigation
        selected={props.selected}
        onSelect={(date) => {
          props.onSelect?.(date);
        }}
        onMonthChange={(date) => {
          setMonth(date);
        }}
        month={month}
        numberOfMonths={1}
        locale={localize}
        endMonth={endOfYear(addYears(new Date(), 50))}
        captionLayout="dropdown"
        components={{
          Dropdown: (props) => {
            return (
              <SelectNavigation {...props} container={calendarRef.current} />
            );
          },
        }}
        formatters={{
          formatWeekdayName: (date) =>
            format(date, 'ccccc', {
              locale: localize,
            }),
        }}
        disabled={props.disabledDates}
        modifiers={{
          eventDays: props.eventDates,
        }}
        modifiersClassNames={{
          eventDays:
            'relative flex flex-col items-end justify-end  before:content-[""] before:absolute before:block before:w-1.5 before:h-1.5 before:rounded-[2px] before:bg-tertiary-light  ',
        }}
        footer={
          <div className="flex place-content-between border-t border-border-secondary p-2">
            <Input
              size={'small'}
              placeholder={t('Date')}
              readOnly
              className="w-28"
              value={formattedDateFrom}
            />
            <Button
              type="button"
              color="primary"
              onClick={handleDateApply}
              disabled={!formattedDateFrom}
            >
              {t('Apply')}
            </Button>
          </div>
        }
      />
    </div>
  );
};
export default CalendarSingle;
